import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Send } from 'lucide-react';

import type { MailSecure, MailSettings, UpdateMailSettingsBody } from '@tasks/contracts';

import { getMailSettings, sendTestMail, updateMailSettings } from '../../api/invitations';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Page, PageHeader } from '../../components/Page';
import { Select, type SelectOption } from '../../components/Select';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import styles from './AdminPages.module.css';

/** Порт по умолчанию для выбранного способа защиты (docs/05 §3a). */
const DEFAULT_PORTS: Record<MailSecure, number> = { tls: 465, starttls: 587 };

const SECURE_OPTIONS: readonly SelectOption<MailSecure>[] = [
  { value: 'tls', label: 'SSL/TLS (обычно 465)' },
  { value: 'starttls', label: 'STARTTLS (обычно 587)' },
];

type MailFormState = {
  readonly isEnabled: boolean;
  readonly host: string;
  readonly port: string;
  readonly secure: MailSecure;
  readonly username: string;
  readonly password: string;
  readonly fromEmail: string;
  readonly fromName: string;
};

function toForm(settings: MailSettings): MailFormState {
  return {
    isEnabled: settings.isEnabled,
    host: settings.host ?? '',
    port: String(settings.port),
    secure: settings.secure,
    username: settings.username ?? '',
    password: '',
    fromEmail: settings.fromEmail ?? '',
    fromName: settings.fromName ?? '',
  };
}

/**
 * UC-ADM-01: настройки почты. Админ вводит SMTP-сервер и ящик, отсюда уходят
 * приглашения. Пароль приложения хранится зашифрованным и не показывается.
 */
export function MailPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<MailFormState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const settingsQuery = useQuery({
    queryKey: queryKeys.admin.mail,
    queryFn: ({ signal }) => getMailSettings(signal),
  });

  useEffect(() => {
    if (settingsQuery.data && form === null) setForm(toForm(settingsQuery.data));
  }, [settingsQuery.data, form]);

  const saveMutation = useMutation({
    mutationFn: (body: UpdateMailSettingsBody) => updateMailSettings(body),
    onSuccess: (settings) => {
      setForm(toForm(settings));
      setError(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.mail });
      toast.success('Настройки почты сохранены');
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      setError(human.message);
      toast.error(human.message, { code: human.code });
    },
  });

  const testMutation = useMutation({
    mutationFn: (to: string | undefined) =>
      sendTestMail(to === undefined || to.length === 0 ? {} : { to }),
    onSuccess: (response) => toast.success(`Письмо отправлено на ${response.to}`),
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      setError(human.message);
      toast.error(human.message, { code: human.code });
    },
  });

  /** Поля тела запроса: пустые строки превращаются в `null` (очистка). */
  const bodyFrom = (state: MailFormState, includePassword: boolean): UpdateMailSettingsBody => ({
    isEnabled: state.isEnabled,
    host: state.host.trim() === '' ? null : state.host.trim(),
    port: Number(state.port) || DEFAULT_PORTS[state.secure],
    secure: state.secure,
    username: state.username.trim() === '' ? null : state.username.trim(),
    ...(includePassword && state.password.length > 0 ? { password: state.password } : {}),
    fromEmail: state.fromEmail.trim() === '' ? null : state.fromEmail.trim(),
    fromName: state.fromName.trim() === '' ? null : state.fromName.trim(),
  });

  if (settingsQuery.isPending || form === null) return <Skeleton variant="row" count={8} />;

  if (settingsQuery.isError) {
    return (
      <Page width="wide">
        <p>Не удалось загрузить настройки: {humanizeError(settingsQuery.error).message}</p>
        <Button variant="primary" onClick={() => void settingsQuery.refetch()}>
          Повторить
        </Button>
      </Page>
    );
  }

  const settings = settingsQuery.data;

  return (
    <Page width="wide">
      <PageHeader
        title="Почта"
        actions={
          <div className={styles['userCell']}>
            <Button
              icon={<Send size={14} />}
              loading={testMutation.isPending}
              onClick={() => testMutation.mutate(undefined)}
            >
              Отправить тестовое письмо
            </Button>
            <Button
              variant="primary"
              loading={saveMutation.isPending}
              onClick={() => {
                setError(null);
                if (form.host.trim().length === 0 || form.fromEmail.trim().length === 0) {
                  setError('Укажите SMTP-сервер и адрес отправителя');
                  return;
                }
                saveMutation.mutate(bodyFrom(form, true));
              }}
            >
              Сохранить
            </Button>
          </div>
        }
      />

      {settings.isConfigured ? null : (
        <div className={styles['formError']} role="status">
          <p>Почта не настроена — приглашения не отправляются.</p>
          {settings.problems.length > 0 ? (
            <ul>
              {settings.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      <div className={styles['form']}>
        <label className={styles['userCell']}>
          <input
            type="checkbox"
            checked={form.isEnabled}
            onChange={(event) => setForm({ ...form, isEnabled: event.target.checked })}
          />
          Отправка писем включена
        </label>

        <Input
          label="SMTP-сервер"
          name="host"
          value={form.host}
          hint="Например, smtp.yandex.ru"
          onChange={(event) => setForm({ ...form, host: event.target.value })}
        />

        <Select
          label="Защита соединения"
          options={SECURE_OPTIONS}
          value={form.secure}
          onChange={(value) => setForm({ ...form, secure: value, port: String(DEFAULT_PORTS[value]) })}
        />

        <Input
          label="Порт"
          name="port"
          inputMode="numeric"
          value={form.port}
          hint="SSL/TLS — 465, STARTTLS — 587"
          onChange={(event) => setForm({ ...form, port: event.target.value })}
        />

        <Input
          label="Логин"
          name="username"
          value={form.username}
          hint="Обычно полный адрес ящика"
          onChange={(event) => setForm({ ...form, username: event.target.value })}
        />

        <Input
          label="Пароль"
          name="password"
          type="password"
          autoComplete="new-password"
          value={form.password}
          hint={
            settings.hasPassword
              ? 'Пароль сохранён. Оставьте пустым, чтобы не менять.'
              : 'Для Яндекса нужен пароль приложения, а не пароль от почты'
          }
          onChange={(event) => setForm({ ...form, password: event.target.value })}
        />

        <Input
          label="Адрес отправителя"
          name="fromEmail"
          type="email"
          value={form.fromEmail}
          hint="Ящик, от имени которого уходят письма"
          onChange={(event) => setForm({ ...form, fromEmail: event.target.value })}
        />

        <Input
          label="Имя отправителя"
          name="fromName"
          value={form.fromName}
          onChange={(event) => setForm({ ...form, fromName: event.target.value })}
        />

        {error ? (
          <p className={styles['formError']} role="alert">
            {error}
          </p>
        ) : null}

        <p className="u-muted">
          <Mail size={12} aria-hidden /> Пароль хранится зашифрованным и наружу не отдаётся.
          {settings.updatedAt ? ` Последнее изменение: ${settings.updatedAt}` : ''}
        </p>
      </div>
    </Page>
  );
}
